import { useState, useEffect } from "react";
import { api } from "@/App";
import { toast } from "sonner";
import {
  Webhook,
  Plus,
  Settings,
  Trash2,
  RefreshCw,
  Check,
  X,
  Copy,
  Play,
  Key,
  Globe,
  Activity,
  AlertCircle,
  CheckCircle,
  Clock,
  ToggleLeft,
  ToggleRight,
  ChevronDown,
  ChevronUp
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";

export default function Webhooks() {
  const [webhooks, setWebhooks] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [expandedWebhook, setExpandedWebhook] = useState(null);
  const [deliveries, setDeliveries] = useState({});
  const [saving, setSaving] = useState(false);
  const [newSecret, setNewSecret] = useState(null);
  
  const [formData, setFormData] = useState({
    url: "",
    description: "",
    events: []
  });

  useEffect(() => {
    fetchWebhooks();
    fetchEvents();
  }, []);

  const fetchWebhooks = async () => {
    setLoading(true);
    try {
      const res = await api.get("/webhooks");
      setWebhooks(res.data.webhooks || []);
    } catch (e) {
      console.error("Error fetching webhooks:", e);
    } finally {
      setLoading(false);
    }
  };

  const fetchEvents = async () => {
    try {
      const res = await api.get("/webhooks/events");
      setEvents(res.data.events || []);
    } catch (e) {
      console.error("Error fetching events:", e);
    }
  };

  const fetchDeliveries = async (webhookId) => {
    try {
      const res = await api.get(`/webhooks/${webhookId}/deliveries`);
      setDeliveries({ ...deliveries, [webhookId]: res.data.deliveries || [] });
    } catch (e) {
      console.error("Error fetching deliveries:", e);
    }
  };

  const toggleExpand = (webhookId) => {
    if (expandedWebhook === webhookId) {
      setExpandedWebhook(null);
    } else {
      setExpandedWebhook(webhookId);
      if (!deliveries[webhookId]) {
        fetchDeliveries(webhookId);
      }
    }
  };

  const handleEventToggle = (eventId) => {
    const current = formData.events;
    if (current.includes(eventId)) {
      setFormData({ ...formData, events: current.filter(e => e !== eventId) });
    } else {
      setFormData({ ...formData, events: [...current, eventId] });
    }
  };

  const handleCreate = async () => {
    if (!formData.url.trim()) {
      toast.error("Webhook URL is required");
      return;
    }
    if (!formData.url.startsWith("https://")) {
      toast.error("URL must use HTTPS");
      return;
    }
    if (formData.events.length === 0) {
      toast.error("Select at least one event");
      return;
    }

    setSaving(true);
    try {
      const res = await api.post("/webhooks", formData);
      setNewSecret(res.data.secret);
      toast.success("Webhook created!");
      fetchWebhooks();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create webhook");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleEnabled = async (webhook) => {
    try {
      await api.put(`/webhooks/${webhook.id}`, { enabled: !webhook.enabled });
      toast.success(webhook.enabled ? "Webhook disabled" : "Webhook enabled");
      fetchWebhooks();
    } catch (e) {
      toast.error("Failed to update webhook");
    }
  };

  const handleTest = async (webhookId) => {
    try {
      const res = await api.post(`/webhooks/${webhookId}/test`);
      toast.success("Test webhook sent!");
      fetchDeliveries(webhookId);
    } catch (e) {
      toast.error("Failed to send test");
    }
  };

  const handleRotateSecret = async (webhookId) => {
    if (!confirm("Rotate the webhook secret? Your endpoint will need to use the new secret.")) return;
    
    try {
      const res = await api.post(`/webhooks/${webhookId}/rotate-secret`);
      setNewSecret(res.data.secret);
      toast.success("Secret rotated!");
    } catch (e) {
      toast.error("Failed to rotate secret");
    }
  };

  const handleDelete = async (webhookId) => {
    if (!confirm("Delete this webhook?")) return;
    
    try {
      await api.delete(`/webhooks/${webhookId}`);
      toast.success("Webhook deleted");
      fetchWebhooks();
    } catch (e) {
      toast.error("Failed to delete webhook");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const closeSecretModal = () => {
    setNewSecret(null);
    setShowCreate(false);
    setFormData({ url: "", description: "", events: [] });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-indigo-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="webhooks">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-indigo-500 flex items-center gap-2">
            <Webhook className="w-7 h-7 text-indigo-400" />
            Webhooks
          </h1>
          <p className="text-indigo-400">Receive real-time notifications for events</p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg transition-colors"
          data-testid="create-webhook-btn"
        >
          <Plus className="w-4 h-4" />
          Add Webhook
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-indigo-500/20 rounded-xl">
                <Webhook className="w-6 h-6 text-indigo-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-indigo-500">{webhooks.length}</p>
                <p className="text-sm text-indigo-400">Total Webhooks</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-green-500/20 rounded-xl">
                <CheckCircle className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-indigo-500">
                  {webhooks.filter(w => w.enabled).length}
                </p>
                <p className="text-sm text-indigo-400">Active</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-indigo-500/20 rounded-xl">
                <Activity className="w-6 h-6 text-indigo-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-indigo-500">{events.length}</p>
                <p className="text-sm text-indigo-400">Available Events</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Webhooks List */}
      {webhooks.length === 0 ? (
        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardContent className="py-12 text-center">
            <Webhook className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-400 mb-2">No webhooks yet</h3>
            <p className="text-slate-400 mb-4">
              Create a webhook to receive real-time event notifications
            </p>
            <button
              onClick={() => setShowCreate(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg"
            >
              <Plus className="w-4 h-4" />
              Create Webhook
            </button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {webhooks.map((webhook) => (
            <Card key={webhook.id} className="bg-slate-800/50 border-slate-700/50">
              <CardContent className="pt-6">
                {/* Webhook Header */}
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Globe className="w-4 h-4 text-indigo-400" />
                      <code className="text-sm text-indigo-500 truncate">{webhook.url}</code>
                      <Badge className={webhook.enabled ? "bg-green-500/20 text-green-400" : "bg-slate-500/20 text-indigo-400"}>
                        {webhook.enabled ? "Active" : "Disabled"}
                      </Badge>
                    </div>
                    {webhook.description && (
                      <p className="text-sm text-indigo-400 mt-1">{webhook.description}</p>
                    )}
                    <div className="flex flex-wrap gap-1 mt-2">
                      {webhook.events?.map((event) => (
                        <Badge key={event} className="bg-slate-400 text-slate-400 text-xs">
                          {event}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleToggleEnabled(webhook)}
                      className="p-2 text-indigo-400 hover:text-indigo-500 hover:bg-slate-400 rounded-lg"
                      title={webhook.enabled ? "Disable" : "Enable"}
                    >
                      {webhook.enabled ? (
                        <ToggleRight className="w-5 h-5 text-green-400" />
                      ) : (
                        <ToggleLeft className="w-5 h-5" />
                      )}
                    </button>
                    <button
                      onClick={() => handleTest(webhook.id)}
                      className="p-2 text-indigo-400 hover:text-indigo-500 hover:bg-slate-400 rounded-lg"
                      title="Test"
                    >
                      <Play className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleRotateSecret(webhook.id)}
                      className="p-2 text-indigo-400 hover:text-indigo-500 hover:bg-slate-400 rounded-lg"
                      title="Rotate Secret"
                    >
                      <Key className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(webhook.id)}
                      className="p-2 text-indigo-400 hover:text-red-400 hover:bg-slate-400 rounded-lg"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => toggleExpand(webhook.id)}
                      className="p-2 text-indigo-400 hover:text-indigo-500 hover:bg-slate-400 rounded-lg"
                    >
                      {expandedWebhook === webhook.id ? (
                        <ChevronUp className="w-4 h-4" />
                      ) : (
                        <ChevronDown className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Expanded Deliveries */}
                {expandedWebhook === webhook.id && (
                  <div className="mt-4 pt-4 border-t border-slate-700">
                    <h4 className="text-sm font-medium text-slate-400 mb-3">Recent Deliveries</h4>
                    {deliveries[webhook.id]?.length === 0 ? (
                      <p className="text-sm text-slate-400">No deliveries yet</p>
                    ) : (
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {deliveries[webhook.id]?.map((delivery) => (
                          <div
                            key={delivery.id}
                            className="flex items-center justify-between p-2 bg-slate-900/50 rounded-lg text-sm"
                          >
                            <div className="flex items-center gap-2">
                              {delivery.status === "success" ? (
                                <CheckCircle className="w-4 h-4 text-green-400" />
                              ) : delivery.status === "failed" ? (
                                <AlertCircle className="w-4 h-4 text-red-400" />
                              ) : (
                                <Clock className="w-4 h-4 text-indigo-400" />
                              )}
                              <span className="text-slate-400">{delivery.event}</span>
                            </div>
                            <span className="text-slate-400 text-xs">
                              {new Date(delivery.created_at).toLocaleString()}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Webhook Modal */}
      <Dialog open={showCreate && !newSecret} onOpenChange={(open) => !open && closeSecretModal()}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-xl">
          <DialogHeader>
            <DialogTitle className="text-indigo-500">Create Webhook</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-slate-400">Endpoint URL</Label>
              <Input
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                placeholder="https://your-server.com/webhook"
                className="bg-slate-800 border-slate-700 text-indigo-500 mt-1 font-mono text-sm"
                data-testid="webhook-url-input"
              />
              <p className="text-xs text-slate-400 mt-1">Must use HTTPS</p>
            </div>
            
            <div>
              <Label className="text-slate-400">Description (optional)</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="What's this webhook for?"
                className="bg-slate-800 border-slate-700 text-indigo-500 mt-1"
              />
            </div>
            
            <div>
              <Label className="text-slate-400 mb-2 block">Events to Subscribe</Label>
              <div className="grid grid-cols-1 gap-2 max-h-48 overflow-y-auto">
                {events.map((event) => (
                  <label
                    key={event.id}
                    className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                      formData.events.includes(event.id)
                        ? "bg-indigo-500/20 border border-indigo-500/50"
                        : "bg-slate-800/50 border border-slate-700 hover:border-slate-600"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={formData.events.includes(event.id)}
                      onChange={() => handleEventToggle(event.id)}
                      className="mt-1"
                    />
                    <div>
                      <p className="text-sm font-medium text-indigo-500">{event.name}</p>
                      <p className="text-xs text-indigo-400">{event.description}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <button
              onClick={closeSecretModal}
              className="px-4 py-2 text-indigo-400 hover:text-indigo-500"
            >
              Cancel
            </button>
            <button
              onClick={handleCreate}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-500 hover:bg-indigo-600 disabled:opacity-50 text-white rounded-lg"
              data-testid="save-webhook-btn"
            >
              {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Create Webhook
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Secret Display Modal */}
      <Dialog open={!!newSecret} onOpenChange={() => setNewSecret(null)}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-indigo-500 flex items-center gap-2">
              <Key className="w-5 h-5 text-indigo-400" />
              Webhook Secret
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <div className="bg-indigo-500/10 border border-indigo-500/30 rounded-lg p-4 mb-4">
              <p className="text-indigo-400 text-sm">
                ⚠️ Save this secret now! It won't be shown again.
              </p>
            </div>
            
            <div className="flex items-center gap-2">
              <code className="flex-1 p-3 bg-slate-800 rounded-lg text-sm text-indigo-500 font-mono break-all">
                {newSecret}
              </code>
              <button
                onClick={() => copyToClipboard(newSecret)}
                className="p-3 bg-slate-400 hover:bg-slate-600 rounded-lg"
              >
                <Copy className="w-4 h-4 text-indigo-500" />
              </button>
            </div>
            
            <p className="text-xs text-slate-400 mt-3">
              Use this secret to verify webhook signatures in your endpoint.
            </p>
          </div>
          
          <DialogFooter>
            <button
              onClick={() => setNewSecret(null)}
              className="px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg"
            >
              Done
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
